import { consume } from '@lit/context';
import { LitElement, css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';

import { flagSvgPath } from '../../../../services/Flags';
import { gameApiContext } from '../../../context';
import type { GameAPIType } from '../../../GameAPI';
import { ANNOUNCEMENT_FADE_OUT_MS, announcementFadeTiming, announcementStyles } from '../../../theme/announcement.css';
import { tokens } from '../../../theme/tokens.css';
import { uiState as uiStateProxy, type UIState } from '../../../UIState';
import { bindValtio } from '../../../ValtioController';

/**
 * BTR Match Result - Battle Royale victory notice overlay
 *
 * Displays when a BTR round ends:
 * - Trophy icon with gentle pulse animation
 * - Winner's name with country flag
 * - Kill count and bounty reward
 * - Auto-dismisses after the server's duration (13s from ab-server), fading out
 *
 * @element btr-match-result
 */
@customElement('btr-match-result')
export class BtrMatchResult extends LitElement {
  static override styles = [
    tokens,
    announcementStyles,
    css`
      /* Positioned, popped in and faded out by the shared .result (the
         trophy's bountyshake is shared too). For a 400×380 panel its transform
         centring is pixel-identical to legacy's -200/-190px margins, and it
         holds as the width cap shrinks it. */
      .panel {
        width: min(400px, calc(100vw - 24px));
        height: 380px;
        background-color: var(--color-bg-hover);
        box-shadow: var(--shadow-panel);
        border-radius: var(--radius-lg);
        border-bottom: 2px solid var(--color-border-strong);
        border-top: 2px solid var(--color-border-light);
        box-sizing: border-box;
        cursor: default;
      }

      .trophy {
        margin: 12px auto 50px auto;
        width: 192px;
        height: 192px;
        background-repeat: no-repeat;
        background-size: 192px 192px;
        background-image: url('/vanilla/ui/gameplay/level-trophy.svg');
        animation: bountyshake 3s ease-in-out infinite;
      }

      .winner {
        height: 70px;
        text-align: center;
      }

      .player {
        color: var(--color-text-white);
        font-weight: bold;
        padding: 5px 14px 4px 58px;
        border-radius: var(--radius-chip);
        background-color: var(--color-bg-chip);
        display: inline-block;
        font-size: 24px;
        line-height: 38px;
        vertical-align: 2px;
        position: relative;
        /* Legacy's fixed 260px cut typical 19–20 character names short, and on
           a 320px phone pushed the pill 36px past the panel. This follows the
           panel instead: 304px of name in the full 400px panel, 12px clear of
           each edge at any width. */
        max-width: calc(100% - 96px);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .flag {
        width: 40px;
        height: 30px;
        background-repeat: no-repeat;
        background-size: 40px auto;
        position: absolute;
        top: 8px;
        left: 9px;
      }

      .stat {
        color: var(--color-text-muted);
        font-weight: bold;
        font-size: var(--font-size-lg);
        height: 30px;
        margin-right: 30px;
      }

      .bounty {
        color: var(--color-text-heading);
        text-align: center;
        font-weight: bold;
        font-size: var(--font-size-xxl);
      }

      /* Short viewports (landscape phones) can't fit the full 380px panel +
         192px trophy. Shrink the trophy and let the panel height follow its
         content so nothing clips. Width is already handled by the min() cap, so
         this keys off height only — a tall narrow phone keeps the full layout. */
      @media (max-height: 420px) {
        /* Centred, its top landed at 52px on a 390px-tall phone, over the
           touch toolbar's bottom edge (58px). 14px lower clears it. */
        .panel {
          top: calc(50% + 14px);
          height: auto;
          padding-bottom: 18px;
        }

        .trophy {
          width: 128px;
          height: 128px;
          background-size: 128px 128px;
          margin: 12px auto 28px auto;
        }
      }
    `,
  ];

  private uiState = bindValtio(this, uiStateProxy);

  @consume({ context: gameApiContext })
  private gameApi!: GameAPIType;

  private dismissTimer: number | null = null;
  private lastResult: UIState['btrMatchResult'] = null;

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.clearDismissTimer();
  }

  private clearDismissTimer() {
    if (this.dismissTimer !== null) {
      window.clearTimeout(this.dismissTimer);
      this.dismissTimer = null;
    }
  }

  override updated() {
    const result = this.uiState.btrMatchResult;

    if (result === this.lastResult) return;
    this.lastResult = result;

    this.clearDismissTimer();

    // No zero-duration exemption: the fade is timed from the same number, so a
    // result that never dismissed would sit there faded out. Legacy dismissed a
    // zero at once too.
    if (result) {
      this.dismissTimer = window.setTimeout(() => {
        this.gameApi.dismissBtrMatchResult();
        this.dismissTimer = null;
        this.lastResult = null;
      }, result.duration * 1000);
    }
  }

  override render() {
    const result = this.uiState.btrMatchResult;

    if (!result) {
      return nothing;
    }

    const killText = result.kills === 1 ? 'KILL' : 'KILLS';
    const flagUrl = flagSvgPath(result.flag);

    return html`
      <div
        class="result panel"
        style=${styleMap(announcementFadeTiming(result.duration * 1000, ANNOUNCEMENT_FADE_OUT_MS))}
      >
        <div class="trophy"></div>
        <div class="winner">
          <div class="player">
            <span class="flag" style="background-image: url('${flagUrl}')"></span>
            ${result.playerName}
          </div>
        </div>
        <div class="bounty">
          <span class="stat">${result.kills} ${killText}</span>
          +${result.bounty} BOUNTY
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'btr-match-result': BtrMatchResult;
  }
}
